import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import bbox from "@turf/bbox";
import { EMPTY_FILTER_ID, IS_AWS_ENV } from "constants/app.constants";
import { EvaChart, RigFracActivityMetric } from "constants/charts.enums";
import { RIG_FRAC_FIELD } from "constants/rigFrac.constants";
import { useChartsContext } from "contexts";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { withRigFracBins } from "entities/charts/eva/requests/rigFracBins";
import { RIG_FRAC_PLASMA_PALETTE } from "entities/charts/eva/requests/rigFracColorPalette";
import { resolveRigFracMetrics } from "entities/charts/eva/requests/rigFracMetrics";
import { createEntityState } from "entities/charts/factory";
import pick from "lodash/pick";
import { updateChart } from "store/features/charts/chartSlice";
import { RootState } from "store/rootReducer";
import { useMapStore } from "stores/map";
import { selectMapExtent } from "stores/map/mapSelectors";
import {
  ChartRequestType,
  ColorPaletteSetting,
  RigFracActivityBbox,
  RigFracActivityChartRequestType,
  RigFracActivityFilters,
  RigFracRecentActivityChartRequestType
} from "types";

import { useRigFracFocusBins } from "hooks/rigfrac/useRigFracFocusBins";
import { useRigFracTypeFilters } from "hooks/rigfrac/useRigFracTypeFilters";

import { usePostRigFracActivityData } from "api/charts/usePostRigFracActivityData";
import { RigFracFilters } from "api/rigfrac";

import { ResultMessageType } from "models/model";

import { useChartEntities } from "./useChartEntities";

// Convention enforced only at these two call sites, not structurally by
// createEntityState: rigFilters/fracFilters must be assigned after
// createEntityState, never passed as an override, or they're silently
// dropped from the request (absent from the DEFAULTS, so the allowlist in
// createMergeWithDefaults' sanitizeObject strips them). This `Omit<>` keeps
// that convention lint-visible.
type RigFracActivityOverrides = Omit<
  Partial<RigFracActivityChartRequestType>,
  "rigFilters" | "fracFilters" | "focusBins" | "foldOthers" | "maxBins"
>;
type RigFracRecentActivityOverrides = Omit<
  Partial<RigFracRecentActivityChartRequestType>,
  "rigFilters" | "fracFilters" | "focusBins" | "foldOthers" | "maxBins"
>;

// Round viewport bounds so sub-pixel pans don't churn the request memo. ~3 dp
// ≈ 100 m — finer than rig/frac point spacing, coarse enough to coalesce jitter.
const roundCoord = (n: number): number => Math.round(n * 1000) / 1000;

const clamp = (n: number, min: number, max: number): number =>
  Math.min(Math.max(n, min), max);

// Record literal keyed on RigFracActivityFilters — exhaustive by construction,
// so a field added to that interface without adding it here is a compile
// error, rather than silently dropped by pick below (same pattern as
// MAIN_FILTER_RIGFRAC_ATTRIBUTES in utils/map/mainFilterRigFrac.ts).
const ACTIVITY_FILTER_FIELDS_MAP: Record<keyof RigFracActivityFilters, true> = {
  operator: true,
  county: true,
  basin: true,
  subBasin: true,
  locationType: true,
  stateProvince: true,
  country: true
};
const ACTIVITY_FILTER_FIELDS = Object.keys(
  ACTIVITY_FILTER_FIELDS_MAP
) as (keyof RigFracActivityFilters)[];
// Azure charts keep ignoring stateProvince and country, as before EVA-338.
const AZURE_ACTIVITY_FILTER_FIELDS = ACTIVITY_FILTER_FIELDS.filter(
  (field) => field !== RIG_FRAC_FIELD.StateProvince && field !== RIG_FRAC_FIELD.Country
);

function pickActivityFilters(filters: RigFracFilters): RigFracActivityFilters {
  return pick(
    filters,
    IS_AWS_ENV ? ACTIVITY_FILTER_FIELDS : AZURE_ACTIVITY_FILTER_FIELDS
  ) as RigFracActivityFilters;
}

// Map viewport polygon → the chart request's bbox. Returns null for an empty or
// degenerate extent (map not yet moved / zero-size hidden map) so chart-svc
// falls back to whole-extent.
//
// Bounds are CLAMPED to ±180 lon / ±90 lat: Mapbox getBounds() returns unwrapped
// longitudes past ±180 at low zoom or across the antimeridian, and chart-svc
// rejects out-of-range boxes with a 400. A viewport spanning the full globe
// (≥360° wide) means "everything" — send null and let the server use its whole
// extent, same as a degenerate box.
function extentToBbox(extent: Maybe<GeoJSON.Polygon>): Nullable<RigFracActivityBbox> {
  if (!extent?.coordinates?.length) return null;
  const [rawMinLon, rawMinLat, rawMaxLon, rawMaxLat] = bbox(extent);
  if (![rawMinLon, rawMinLat, rawMaxLon, rawMaxLat].every(Number.isFinite)) return null;
  if (rawMaxLon - rawMinLon >= 360) return null;
  const minLon = clamp(rawMinLon, -180, 180);
  const maxLon = clamp(rawMaxLon, -180, 180);
  const minLat = clamp(rawMinLat, -90, 90);
  const maxLat = clamp(rawMaxLat, -90, 90);
  // Degenerate AFTER clamping too — a viewport entirely outside the valid range
  // collapses to a zero-width box, which must also fall back to whole-extent.
  if (minLon === maxLon || minLat === maxLat) return null;
  return {
    minLon: roundCoord(minLon),
    minLat: roundCoord(minLat),
    maxLon: roundCoord(maxLon),
    maxLat: roundCoord(maxLat)
  };
}

// All selectable fields across BOTH rig/frac charts, all optional — the entity
// request may hold either shape (or a stale one mid chart-switch), so read
// leniently and rebuild cleanly per the ACTIVE chartType below.
type RigFracSelections = Partial<RigFracActivityChartRequestType> &
  Partial<RigFracRecentActivityChartRequestType>;

/**
 * Slim data controller for BOTH Rig/Frac charts (EVA-76): Activity Over Time
 * (stacked bars + line, left/right metrics, time-scale) and Recent Activity
 * (single metric, ranked horizontal bars). Builds the lean request per the
 * active chartType and POSTs via the shared endpoint.
 *
 * Deliberately NOT a fork of useChartDataController: these charts have no wells
 * dependencies — no products, forecast, normalization or group-by service. The
 * request is built purely from the selections stored on the chart entity's
 * request object, defaulted per chart, plus (AWS permits only) the well filterId.
 */
export function useRigFracActivityController() {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const { chartType, request: entityRequest } = useChartEntities(id);

  const isRecent = chartType === EvaChart.RigFracRecentActivity;
  const isRigFrac = chartType === EvaChart.RigFracActivity || isRecent;

  // The entity request field is typed as the wells ChartRequestType; these
  // charts store their own lean request there (same cast pattern as
  // useChartDataController).
  const selections = entityRequest as unknown as Maybe<RigFracSelections>;

  const { primaryMetric, secondaryMetric, metric } = resolveRigFracMetrics(selections);
  const groupBy = selections?.groupBy ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy;
  const timeScale = selections?.timeScale ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.timeScale;
  const customRangeFrom = selections?.customRange?.from;
  const customRangeTo = selections?.customRange?.to;
  const maxBins = selections?.maxBins;

  const focusSelection = useRigFracFocusBins(groupBy, isRigFrac);
  const focusBins = focusSelection?.focusBins;
  const foldOthers = focusSelection?.foldOthers;
  const focusBinsKey = useMemo(
    () => (focusBins ? JSON.stringify(focusBins) : undefined),
    [focusBins]
  );

  // Bars are colored server-side from this palette. Default to Plasma for the
  // rig/frac charts when the entity request has none set (rather than the user's
  // active wells palette); the shapes overlap on the fields the backend reads,
  // so cast at this seam.
  const colorPalette = (selections?.colorPalette ??
    RIG_FRAC_PLASMA_PALETTE) as unknown as ColorPaletteSetting;

  // The chart scopes to the current map viewport; panning/zooming the map
  // refetches. The 300ms debounce + abort in usePostRigFracActivityData
  // coalesces mid-pan changes.
  const mapExtent = useMapStore(selectMapExtent);
  const viewportBbox = useMemo(() => extentToBbox(mapExtent), [mapExtent]);

  const { rigFilters, fracFilters } = useRigFracTypeFilters();
  const rigActivityFilters = pickActivityFilters(rigFilters);
  const fracActivityFilters = pickActivityFilters(fracFilters);

  // Omit empty filter objects from the POST body entirely (rather than
  // sending `{}`) — the backends key filter-observability off whether the
  // field is present at all, not whether it's empty.
  const rigFiltersForRequest = Object.keys(rigActivityFilters).length
    ? rigActivityFilters
    : undefined;
  const fracFiltersForRequest = Object.keys(fracActivityFilters).length
    ? fracActivityFilters
    : undefined;

  // filtersKey can carry fields this chart never sends (pickActivityFilters
  // drops them) — keying on the raw filtersKey would rebuild `request` and
  // re-POST a byte-identical body on an edit to one of those. This key covers
  // exactly what the request body actually carries.
  const projectedFiltersKey = JSON.stringify([rigActivityFilters, fracActivityFilters]);

  // EVA-105. Permits are counted from the well filter, so chart-svc needs its id —
  // but only then: keying every request on it would refetch rig/frac-only charts
  // on each filter edit. The sentinel means "no filter", sent as an absent key.
  const rawFilterId = useSelector((state: RootState) => state.filter.filterId?.id);
  const usesPermits =
    IS_AWS_ENV &&
    (isRecent ? [metric] : [primaryMetric, secondaryMetric]).includes(
      RigFracActivityMetric.ApprovedPermits
    );
  const permitFilterId =
    usesPermits && rawFilterId && rawFilterId !== EMPTY_FILTER_ID
      ? rawFilterId
      : undefined;

  const request = useMemo(() => {
    if (!isRigFrac) return null;
    // colorPalette is assigned AFTER createEntityState (not passed as an override)
    // so it's copied wholesale — createMergeWithDefaults/lodash would otherwise
    // array-index-merge the picked palette's colors/colorScale against the Jet
    // default, leaking Jet's trailing entries. rigFilters/fracFilters are
    // assigned the same way, for the same reason: they're string arrays, so a
    // shorter new selection passed as an override would leak the
    // previous/default array's trailing entries into the POST.
    if (isRecent) {
      const recentOverrides: RigFracRecentActivityOverrides = {
        metric,
        groupBy,
        bbox: viewportBbox
      };
      const recentReq = createEntityState(
        EvaChart.RigFracRecentActivity,
        recentOverrides
      ) as RigFracRecentActivityChartRequestType;
      recentReq.colorPalette = colorPalette;
      recentReq.rigFilters = rigFiltersForRequest;
      recentReq.fracFilters = fracFiltersForRequest;
      if (permitFilterId) recentReq.filterId = permitFilterId;
      return withRigFracBins(recentReq, focusSelection, maxBins);
    }
    const activityOverrides: RigFracActivityOverrides = {
      primaryMetric,
      secondaryMetric,
      groupBy,
      timeScale,
      customRange:
        customRangeFrom && customRangeTo
          ? { from: customRangeFrom, to: customRangeTo }
          : null,
      bbox: viewportBbox
    };
    const activityReq = createEntityState(
      EvaChart.RigFracActivity,
      activityOverrides
    ) as RigFracActivityChartRequestType;
    activityReq.colorPalette = colorPalette;
    activityReq.rigFilters = rigFiltersForRequest;
    activityReq.fracFilters = fracFiltersForRequest;
    if (permitFilterId) activityReq.filterId = permitFilterId;
    return withRigFracBins(activityReq, focusSelection, maxBins);
    // Scalar deps only: the updateChart(request) echo below replaces the entity
    // request object, and object deps would rebuild the memo → re-POST → loop.
    // bbox is depended on via its rounded scalar coords, not the object identity;
    // the palette via its id AND reverse — the picker's reverse control returns
    // the SAME palette (same id) with only `reverse` flipped, so id alone would
    // silently drop the re-POST and the bars would never re-color.
    // `projectedFiltersKey` is the only filter-related dep — it is a content
    // key over exactly the fields this request sends, so an edit to a field
    // it doesn't send (part of filtersKey, not of the request) can't rebuild this.
    // The focus bins are a list, so they are depended on by content.
  }, [
    isRigFrac,
    isRecent,
    metric,
    primaryMetric,
    secondaryMetric,
    groupBy,
    timeScale,
    customRangeFrom,
    customRangeTo,
    viewportBbox?.minLon,
    viewportBbox?.minLat,
    viewportBbox?.maxLon,
    viewportBbox?.maxLat,
    colorPalette?.id,
    colorPalette?.reverse,
    projectedFiltersKey,
    permitFilterId,
    focusBinsKey,
    foldOthers,
    maxBins
  ]);

  const { data: apiResponse, loading, error } = usePostRigFracActivityData(request);

  // usePostRigFracActivityData debounces the actual fetch, so `loading` stays
  // false for up to 300ms after a filter edit while `apiResponse` still holds
  // the PREVIOUS filter's result. Track which projectedFiltersKey the
  // currently-held apiResponse actually landed for — updated synchronously
  // during render (not in an effect) so the very render that receives a new
  // apiResponse already has the correct pairing, rather than lagging a render
  // behind. Keyed on projectedFiltersKey, not the raw filtersKey: an edit to
  // an unsent field never produces a new apiResponse (see `request` above),
  // so comparing against filtersKey here would wrongly and permanently read
  // as "stale" after one.
  const lastApiResponseRef = useRef<typeof apiResponse>(undefined);
  const responseFiltersKeyRef = useRef<Maybe<string>>(undefined);
  if (apiResponse !== lastApiResponseRef.current) {
    lastApiResponseRef.current = apiResponse;
    responseFiltersKeyRef.current = projectedFiltersKey;
  }

  // The anchor GetPoints calls stay unfiltered so the x-axis never rebases on
  // a filter edit — so an all-zero series can mean either "no data anywhere"
  // or "matches exist, just outside this window", and can't be told apart
  // client-side. The flag is worded to be honest in either case rather than
  // asserting the window-specific reading.
  //
  // Only applies to the time-bucketed Activity Over Time chart (Recent
  // Activity has no time buckets), and gated on `!loading` so it doesn't
  // overlay the BusyIndicator while a new filter's request is in flight.
  const hasFilterSelection = !!rigFiltersForRequest || !!fracFiltersForRequest;
  const showNoMatchInWindow = useMemo(() => {
    if (isRecent || loading || !hasFilterSelection) return false;
    // The response in hand hasn't caught up to the currently active filter
    // yet (see the ref above) — say nothing rather than asserting a stale
    // "matches" reading about a filter that's no longer applied.
    if (responseFiltersKeyRef.current !== projectedFiltersKey) return false;
    // Mutually exclusive with both of the wrapper's own message branches, so
    // two explanations can never stack over one chart. NoData: the service
    // could stamp it while still returning zero-filled series, since the
    // unfiltered anchor query guarantees the buckets exist. GeneralError: a
    // backend failure arrives as HTTP 200 with that message type, so the
    // controller's `error` flag does not catch it — and "no activity matches
    // your filter" is a badly wrong diagnosis of an outage.
    if (
      apiResponse?.messageType === ResultMessageType.NoData ||
      apiResponse?.messageType === ResultMessageType.GeneralError
    ) {
      return false;
    }
    const series = apiResponse?.series;
    if (!series || series.length === 0) return false;
    // Every series must carry at least one bucket — `.every()` on an empty
    // array is vacuously true, which would otherwise report an empty-bucket
    // response (unrelated to filtering) as "all zero" — and every value must
    // be a genuine numeric zero, not null/undefined/NaN (which `!v` would
    // otherwise treat the same as 0).
    return series.every((s) => {
      const y = s.y ?? [];
      return y.length > 0 && y.every((v) => v === 0);
    });
    // responseFiltersKeyRef/lastApiResponseRef are refs (not reactive), but
    // they're only ever updated in lockstep with apiResponse above, so
    // depending on apiResponse + projectedFiltersKey is sufficient to
    // re-derive this.
  }, [isRecent, loading, hasFilterSelection, apiResponse, projectedFiltersKey]);

  // Mirror useChartDataController's response handling: persist request + response
  // on the chart entity so the render/legend/screenshot paths pick them up.
  //
  // Deps are INTENTIONALLY [apiResponse] only — NOT request/chartType/id/dispatch.
  // This echo must fire once, when a response lands, persisting that response
  // together with the request that produced it. The abort-on-change in
  // usePostRigFracActivityData guarantees a new apiResponse always corresponds to
  // the latest request, so the current request/chartType closure is the correct
  // pairing. Adding `request` (a NEW object on every memo rebuild) would fire this
  // on each selection change with the PREVIOUS apiResponse, briefly persisting a
  // mismatched request+response pair. id/dispatch are stable. The explicit
  // disable below makes the intent lint-proof (documented so the narrow dep
  // list isn't "corrected" into a bug).
  useEffect(() => {
    if (!apiResponse || !request) return;
    dispatch(
      updateChart({
        id,
        chartType,
        request: request as unknown as ChartRequestType,
        response: apiResponse
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse]);

  return { loading, error, apiResponse, showNoMatchInWindow };
}
